'use client';

import { createHttpClient, HttpError, readXsrfCookie } from '@/lib/api/httpClient';
import type {
  KycConfig,
  KycExtractResult,
  KycStatusResponse,
  KycSubmitFiles,
} from '@/lib/api/kyc/types';

// KYC service base URL. Falls back to same-origin (gateway routes /api/kyc) when unset.
const BASE_URL = process.env.NEXT_PUBLIC_API_KYC_URL ?? '';

const kycHttp = createHttpClient({ baseUrl: BASE_URL });

/** `issuing` narrows the document types to the ones accepted for that issuing country. */
export async function getKycConfig(locale: string, issuing?: string): Promise<KycConfig> {
  return kycHttp.get<KycConfig>('/api/kyc/config', {
    query: issuing ? { locale, issuing } : { locale },
  });
}

export async function getKycStatus(locale?: string): Promise<KycStatusResponse> {
  return kycHttp.get<KycStatusResponse>('/api/kyc/status', {
    query: locale ? { locale } : undefined,
  });
}

// The raw-fetch selfie upload throws the same HttpError the shared client does.
async function toHttpError(res: Response): Promise<HttpError> {
  const isJson = (res.headers.get('content-type') ?? '').includes('application/json');
  const payload = isJson ? await res.json().catch(() => undefined) : await res.text();
  return asTooLarge(
    new HttpError(`Request failed with status ${res.status}`, res.status, res.url, payload),
  );
}

// A body over the proxy's limit is cut by nginx with an HTML 413 before kyc-service answers.
// Give it the service's own "file too large" shape, so the UI shows that instead of raw HTML.
const FILE_TOO_LARGE = 'KYC-2008';

function asTooLarge(error: HttpError): HttpError {
  if (error.status !== 413) return error;
  return new HttpError(error.message, 413, error.url, {
    status: 413,
    e: FILE_TOO_LARGE,
    message: 'File is too large.',
  });
}

async function upload<T>(request: Promise<T>): Promise<T> {
  try {
    return await request;
  } catch (e) {
    throw e instanceof HttpError ? asTooLarge(e) : e;
  }
}

/**
 * Submit KYC documents (multipart). Goes through the shared client, so an access token that
 * expired while the user filled the form is refreshed and the upload retried instead of a 401.
 */
export async function submitKyc(
  files: KycSubmitFiles,
  declared?: string,
  mobileSessionId?: string,
  screenTimings?: string,
): Promise<void> {
  const form = new FormData();
  for (const [key, file] of Object.entries(files)) {
    if (file) form.append(key, file);
  }
  if (declared) form.append('declared', declared);
  // Selfie captured on the phone (QR handoff) — the backend pulls it from the session.
  if (mobileSessionId) form.append('mobileSessionId', mobileSessionId);
  // Seconds per wizard step — shown to admins in the application detail.
  if (screenTimings) form.append('screenTimings', screenTimings);
  await upload(kycHttp.post<void>('/api/kyc/submit', { body: form }));
}

/** QR handoff — desktop: create a mobile session (its id goes into the QR the phone scans). */
export async function createMobileSession(): Promise<{ sessionId: string; expiresAt: string }> {
  return kycHttp.post<{ sessionId: string; expiresAt: string }>('/api/kyc/mobile-session');
}

/** QR handoff — desktop: poll the session (PENDING | CAPTURED | CONSUMED | EXPIRED). */
export async function getMobileSessionStatus(sessionId: string): Promise<{ status: string }> {
  return kycHttp.get<{ status: string }>(`/api/kyc/mobile-session/${sessionId}`);
}

/**
 * QR handoff — phone: upload the captured selfie against a session id (token-authorised, no login).
 * Raw fetch on purpose: the shared client would treat a 401 here as an expired login and log out.
 */
export async function captureMobileSelfie(sessionId: string, selfie: File): Promise<void> {
  const form = new FormData();
  form.append('selfie', selfie);
  const res = await fetch(`${BASE_URL}/api/kyc/mobile-session/${sessionId}/selfie`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-XSRF-TOKEN': readXsrfCookie() },
    body: form,
  });
  if (!res.ok) throw await toHttpError(res);
}

/**
 * Vision-only extraction to pre-fill the Confirm step: send the document photo(s), get back the
 * identity fields the AI reads. Does not create an application.
 */
export async function extractKyc(
  front: File,
  back: File | null,
  docType: string,
): Promise<KycExtractResult> {
  const form = new FormData();
  form.append('idDoc', front);
  if (back) form.append('idDocBack', back);
  // The server rejects a photo of another document type than the one chosen on Start.
  form.append('docType', docType);
  return upload(kycHttp.post<KycExtractResult>('/api/kyc/extract', { body: form }));
}
